import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  Typography,
} from "@mui/material";
import React, {
  Fragment,
  useEffect,
  useMemo,
  useState,
} from "react";
import { SouthRounded, NorthRounded, UnfoldMoreRounded } from "@mui/icons-material";
import { useAppContext } from "context";
import { API } from "../../api/api";
import { convertToUtc, getTimeZone, ToFormData } from "../../hooks/Method";
import DeepSearch from "./DeepSearch";
import Export from "./Export";
import { DateRange, InputCheck } from "controls";
import { toast } from "react-toastify";
import { useTranslation } from "react-i18next";
import dayjs from "dayjs";

//  Helper: remove empty / null filters before API call
const cleanParams = (obj) =>
  Object.fromEntries(
    Object.entries(obj).filter(
      ([_, v]) => v !== "" && v !== null && v !== undefined,
    ),
  );

function DynamicTable({
  id = "",
  enableExport = false,
  enableDateRange = false,
  showDefaultDate = false,
  defaultDate,
  headers = [],
  setRecords,
  records = [],
  HeaderComp = <div></div>,
  ActionComp = <div></div>,
  dynamicSearch = true,
  FooterRow,
  endpointKey,
  exportEndpoint,
  pdfExportEndpoint,
  exportFormats = ["excel"],
  fetchData,
  onExport,
  enableSelect = false,
  setSelected,
  selected,
  setResult,
  disableHeader = false,
  disableLoader = false,
  onDateRangeChange,

  // Opt-in column sorting. A header with `sortable: true` becomes clickable and
  // shows a ↑/↓ arrow; clicking calls onHeaderSort(columnId). The PARENT owns
  // the sortBy/sortDir state (and passes them into otherParams to drive the
  // backend). Headers without `sortable` are unaffected.
  sortBy,
  sortDir,
  onHeaderSort,

  // Optional custom control(s) rendered in the right-hand toolbar cluster
  // (before Export / Date / Search) — e.g. a prominent sort-order toggle.
  // Node or () => node. Undefined renders nothing.
  toolbarExtra,

  otherParams = {
    outletId: null,
    saleType: null,
    jvStatus: null,
    jvType: null,
    isVariant: null,
  },
}) {
  const maxSelect = enableSelect ? 100 : 0; // Maximum number of records that can be selected at once
  const { authUser, setLoader, selCompany } = useAppContext();
  const { t } = useTranslation();

  const [localLoading, setLocalLoading] = useState(true);
  const [pageNum, setPageNum] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [searchVal, setSearchVal] = useState("");
  const [totalRecord, setTotalRecord] = useState(null);
  const [maxSelectItems, setMaxSelectItems] = useState([]);

  const [sortDate, setSortDate] = useState({
    fromDate: showDefaultDate
      ? defaultDate?.fromDate || dayjs().startOf("month")
      : "",
    toDate: showDefaultDate
      ? defaultDate?.toDate || dayjs().endOf("month")
      : "",
  });

  useEffect(() => {
    onDateRangeChange && onDateRangeChange(sortDate);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortDate.fromDate, sortDate.toDate]);

  // Normalize filters (critical fix)
  const normalizedOtherParams = useMemo(
    () => cleanParams(otherParams),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      otherParams.outletId,
      otherParams.saleType,
      otherParams.jvStatus,
      otherParams.jvType,
      otherParams.isVariant,
      // Promotion-engine filters
      otherParams.rewardType,
      otherParams.statusFilter,
      // Report filters
      otherParams.supplierId,
      otherParams.categoryId,
      otherParams.brandId,
      otherParams.customerId,
      otherParams.customerGroupId,
      otherParams.sortBy,
      otherParams.sortDir,
      otherParams.balanceFilter,
      // Expense report filters
      otherParams.expenseTypeId,
      otherParams.source,
      otherParams.groupBy,
      otherParams.month,
      // Sales history register-scope filter
      otherParams.sellRegId,
      // Item-wise sales drill-down
      otherParams.prodId,
      otherParams.varProdId,
      otherParams.fromDate,
      otherParams.toDate,
      otherParams.cashierId,
      otherParams.profitabilityFilter,
      // Payment Report filters
      otherParams.payTypeId,
      otherParams.direction,
      // Sales Summary filters
      otherParams.categoryId,
      otherParams.brandId,
      otherParams.sellType,
      otherParams.paymentStatus,
    ],
  );

  const param = useMemo(
    () =>
      cleanParams({
        id,
        pageNum,
        rowsPerPage,
        searchVal: searchVal || null,
        fromDate: sortDate.fromDate
          ? convertToUtc(new Date(sortDate.fromDate))
          : null,
        toDate: sortDate.toDate
          ? convertToUtc(new Date(sortDate.toDate))
          : null,
        today: convertToUtc(new Date()),
        timeZone: getTimeZone(),
        companyId: selCompany || null,
        maxSelect,
        ...normalizedOtherParams,
      }),
    [
      id,
      pageNum,
      rowsPerPage,
      searchVal,
      sortDate.fromDate,
      sortDate.toDate,
      selCompany,
      maxSelect,
      normalizedOtherParams,
    ],
  );

  // const fetchRecords = () => {
  //   if (!disableLoader) {
  //     setLoader(true);
  //     setLocalLoading(true);
  //   }

  //   API(endpointKey).post(
  //     ToFormData({
  //       ...param,
  //       fromDate: convertToUtc(param.fromDate),
  //       toDate: convertToUtc(param.toDate),
  //       today: convertToUtc(param.today),
  //       maxSelect,
  //       companyId: selCompany || "",
  //     }),
  //     (res) => {
  //       if (res && res.status === 200) {
  //         setRecords([...res.result]);
  //         setTotalRecord(res.totalRecord || 0);
  //         setMaxSelectItems(res.maxSelectItems || []);
  //         setResult && setResult({ ...res });
  //       } else setRecords([]);

  //       setLoader(false);
  //       setLocalLoading(false);
  //     },
  //   );
  // };

  const fetchRecords = async () => {
    if (!disableLoader) {
      setLoader(true);
      setLocalLoading(true);
    }

    const handleResult = (res) => {
      if (res?.status === 200) {
        setRecords(res.result || []);
        setTotalRecord(res.totalRecord || 0);
        setMaxSelectItems(res.maxSelectItems || []);
        setResult?.(res);
      } else {
        setRecords([]);
        setTotalRecord(0);
      }
      setLoader(false);
      setLocalLoading(false);
    };

    if (fetchData) {
      try {
        const res = await fetchData(param);
        handleResult(res);
      } catch {
        handleResult(null);
      }
    } else {
      API(endpointKey).post(ToFormData(param), handleResult);
    }
  };

  // Effect: main reload triggers
  useEffect(() => {
    if (authUser) fetchRecords();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    authUser,
    pageNum,
    rowsPerPage,
    sortDate.fromDate,
    sortDate.toDate,
    endpointKey,
    id,
    selCompany,
    normalizedOtherParams,
  ]);

  // useEffect(() => {
  //   authUser && fetchRecords();
  //   return () => {};
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [authUser, pageNum, rowsPerPage, sortDate, endpointKey, id, selCompany]);

  // useEffect(() => {
  //   if (searchVal.length <= 0 && authUser) {
  //     setPageNum(0);
  //     fetchRecords();
  //   }

  //   return () => {};
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [searchVal]);

  useEffect(() => {
    if (searchVal.length === 0 && authUser) {
      setPageNum(0);
      fetchRecords();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchVal]);

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(+event.target.value);
    setPageNum(0);
  };

  const handleChangePage = (event, newPage) => {
    setPageNum(newPage);
  };

  const handleSearch = (e) => {
    e.preventDefault();
    fetchRecords(0);
  };

  const handleRecSelect = (e, item) => {
    if (selected.items.length >= maxSelect && e.target.checked) {
      toast.error(t("maxSelectWarning", { max: maxSelect }));
      return;
    } else if (e.target.checked) {
      setSelected((prev) => ({
        ...prev,
        items: [...prev.items, item.id],
      }));
    } else
      setSelected((prev) => ({
        ...prev,
        items: prev.items.filter((i) => i !== item.id),
      }));
  };

  const handleSelectAll = (e) => {
    var checked = e.target.checked;
    if (checked) {
      setSelected((prev) => ({
        ...prev,
        items: [...maxSelectItems],
        all: checked,
      }));
      if (totalRecord > maxSelect) {
        toast.warning(t("maxSelectInfo", { max: maxSelect }));
      }
    } else setSelected((prev) => ({ ...prev, all: checked, items: [] }));
  };

  return (
    <>
      {!disableHeader && (
        <Box className="flex justify-between items-center flex-wrap mb-[15px] gap-2 bg-white p-[5px] rounded-[18px]">
          <Box className="flex flex-1 min-w-0 basis-full md:basis-auto">
            {/* Invoke as a function when passed as one, so a new arrow-function
                identity on every parent render does NOT cause a remount (which
                would wipe inner filter state). */}
            {React.isValidElement(HeaderComp)
              ? HeaderComp
              : typeof HeaderComp === "function"
              ? HeaderComp()
              : null}
          </Box>
          {dynamicSearch && (
            <div className="flex flex-wrap gap-2 w-full md:w-auto items-center">
              {toolbarExtra &&
                (typeof toolbarExtra === "function" ? toolbarExtra() : toolbarExtra)}
              {enableExport && (
                <Export
                  endpoint={exportEndpoint}
                  pdfEndpoint={pdfExportEndpoint}
                  formats={exportFormats}
                  value={param}
                  onExport={onExport}
                />
              )}
              {enableDateRange && <DateRange {...{ sortDate, setSortDate }} />}
              <DeepSearch {...{ searchVal, setSearchVal, handleSearch }} />
            </div>
          )}
        </Box>
      )}

      {/* Table  */}
      <Paper
        sx={{
          width: "100%",
          overflow: "hidden",
        }}
      >
        <TableContainer sx={{ maxHeight: "calc(100vh - 184px)" }}>
          <Table stickyHeader>
            <TableHead>
              <TableRow>
                {enableSelect && (
                  <TableCell>
                    <InputCheck
                      checked={selected.all}
                      onChange={handleSelectAll}
                      sx={{
                        "& .MuiButtonBase-root": { padding: "0px", ml: "10px" },
                      }}
                    />
                  </TableCell>
                )}

                {headers.map((item, i) => {
                  const canSort = item.sortable && typeof onHeaderSort === "function";
                  const isActive = canSort && sortBy === item.id;
                  return (
                    <TableCell
                      key={i}
                      onClick={canSort ? () => onHeaderSort(item.id) : undefined}
                      sx={{
                        fontSize: "14px",
                        backgroundColor: "var(--white-color)",
                        color: "var(--primary-color)",
                        borderBottom: "1px solid var(--primaryMD-color)",
                        minWidth: "80px",
                        textTransform: "uppercase",
                        whiteSpace: "nowrap",
                        padding: "16px 8px 16px 16px",
                        ...(canSort && {
                          cursor: "pointer",
                          userSelect: "none",
                          "&:hover": { backgroundColor: "var(--primaryXS-color)" },
                        }),
                      }}
                    >
                      {canSort ? (
                        <span className="inline-flex items-center gap-1">
                          {item.label}
                          {isActive ? (
                            sortDir === "asc" ? (
                              <NorthRounded sx={{ fontSize: 16 }} />
                            ) : (
                              <SouthRounded sx={{ fontSize: 16 }} />
                            )
                          ) : (
                            <UnfoldMoreRounded sx={{ fontSize: 16, opacity: 0.35 }} />
                          )}
                        </span>
                      ) : (
                        item.label
                      )}
                    </TableCell>
                  );
                })}
              </TableRow>
            </TableHead>
            <TableBody>
              {!localLoading &&
                records &&
                records.map((item, i) => {
                  return (
                    <TableRow hover key={i}>
                      {/* ------- Checkbox for Select -------  */}
                      {enableSelect && (
                        <TableCell>
                          <InputCheck
                            checked={selected.items?.some((s) => s === item.id)}
                            name={`${item.id}${item.label}`}
                            onChange={(e) => handleRecSelect(e, item)}
                            sx={{
                              "& .MuiButtonBase-root": {
                                padding: "0px",
                                ml: "10px",
                              },
                            }}
                          />
                        </TableCell>
                      )}
                      {headers.map((headItem, index) => {
                        return (
                          <Fragment key={index}>
                            {/* ------- Table Data -------  */}
                            <TableCell
                              key={index}
                              sx={{
                                fontSize: "16px",
                                padding: "6px 16px 6px 16px",
                                whiteSpace: "nowrap",
                                verticalAlign: "top",
                                color: "var(--gray-color)",
                              }}
                            >
                              {headItem.id === "actions" ? (
                                <ActionComp {...item} />
                              ) : (
                                item[headItem.id]
                              )}
                            </TableCell>
                          </Fragment>
                        );
                      })}
                    </TableRow>
                  );
                })}

              {FooterRow && <TableRow>{<FooterRow />}</TableRow>}
            </TableBody>
          </Table>

          {localLoading && (
            <Typography
              variant="h6"
              className="tx-center p-30 tx-upp gray-color"
            >
              {t("loadingDots")}
            </Typography>
          )}

          {!totalRecord && !localLoading && (
            <Typography
              variant="h6"
              className="tx-center p-30 tx-upp gray-color"
            >
              {t("dataNotAvailable")}
            </Typography>
          )}
        </TableContainer>
        {totalRecord > 0 && !localLoading && (
          <TablePagination
            rowsPerPageOptions={[10, 20, 50, 100]}
            component="div"
            labelRowsPerPage={t("row")}
            count={totalRecord}
            rowsPerPage={rowsPerPage}
            page={pageNum}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
          />
        )}
      </Paper>
    </>
  );
}

export default DynamicTable;
